import { useTranslation } from "react-i18next";
import { Icon, cn } from "@nexus/ui";
import { KPI_COLOR_TOKENS, type KpiColorToken } from "@nexus/kpi-formula";

const CORE_TOKEN_COUNT = 15;

interface KpiColorPickerProps {
  value: KpiColorToken;
  onChange: (token: KpiColorToken) => void;
  disabled?: boolean;
}

function ColorSwatch({
  token,
  selected,
  disabled,
  label,
  onChange,
}: {
  token: KpiColorToken;
  selected: boolean;
  disabled?: boolean;
  label: string;
  onChange: (token: KpiColorToken) => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(token)}
      className={cn(
        "relative flex size-11 items-center justify-center rounded-lg",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        selected && "ring-2 ring-ring ring-offset-2",
      )}
      style={{ backgroundColor: `var(--${token})` }}
    >
      {selected ? (
        <Icon name="check" size="sm" color="inherit" className="text-white drop-shadow-sm" />
      ) : null}
    </button>
  );
}

export function KpiColorPicker({ value, onChange, disabled }: KpiColorPickerProps) {
  const { t } = useTranslation();
  const coreTokens = KPI_COLOR_TOKENS.slice(0, CORE_TOKEN_COUNT);
  const extraTokens = KPI_COLOR_TOKENS.slice(CORE_TOKEN_COUNT);

  return (
    <div
      role="radiogroup"
      aria-label={t("kpiSettings.colorPicker", "KPI color")}
      className="flex flex-col gap-3"
    >
      <div className="flex flex-wrap gap-2">
        {coreTokens.map((token) => (
          <ColorSwatch
            key={token}
            token={token}
            selected={token === value}
            disabled={disabled}
            label={t(`kpiSettings.colors.${token}`, token)}
            onChange={onChange}
          />
        ))}
      </div>
      {extraTokens.length > 0 ? (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-muted-foreground">
            {t("kpiSettings.moreColors", "More colors")}
          </p>
          <div className="flex flex-wrap gap-2">
            {extraTokens.map((token) => (
              <ColorSwatch
                key={token}
                token={token}
                selected={token === value}
                disabled={disabled}
                label={t(`kpiSettings.colors.${token}`, token)}
                onChange={onChange}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
